import * as React from 'react';
import { Form as SharedForm } from '@components/shared/forms';
import { Image } from '@root/types';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { object, SchemaOf, string, number, array, boolean } from 'yup';
import { FormCancellationPrompt, ImagesContextProvider } from '.';

type Props = {
  tourbookId: string;
  children: React.ReactNode;
  initialValues?: Partial<FormValues>;
  isPromptEnabled: boolean;
  onSubmit: (values: FormValues) => void;
};

export type FormValues = {
  buildingName: string | undefined | null;
  addressLine1: string;
  addressLine2: string | undefined | null;
  city: string;
  state: string;
  zipCode: string;
  longitude: number | null;
  latitude: number | null;
  countryCode: string;
  photos: Image[];
  size: number | '' | null;
  askingRent: undefined | number;
  askingRentUnits: string;
  condition: string | undefined | null;
  leaseType: string;
  description: string | undefined | null;
  availabilityDate: string | null | undefined;
  availableImmediately: boolean;
  location?: string;
  submarket: string | null | undefined;
};

const validationSchema = (t: TFunction): SchemaOf<FormValues> =>
  object({
    // location tab
    buildingName: string()
      .nullable()
      .max(30, t('externalListing.buildingNameMax'))
      .label(t('externalListing.buildingName')),
    addressLine1: string()
      .max(30, t('externalListing.addressMax'))
      .required()
      .label(t('externalListing.addressLabel')),
    addressLine2: string()
      .required()
      .max(30, t('externalListing.address2Max'))
      .label(t('externalListing.address2Label')),
    city: string()
      .max(20, t('externalListing.cityMax'))
      .required()
      .label(t('externalListing.cityLabel')),
    state: string()
      .max(20, t('externalListing.stateMax'))
      .nullable()
      .required()
      .label(t('externalListing.stateLabel')),
    zipCode: string()
      .max(5, t('externalListing.zipCodeMax'))
      .required()
      .label(t('externalListing.zipLabel')),
    longitude: number().nullable().required(),
    latitude: number().nullable().required(),
    countryCode: string().required().default('US'),
    // media tab
    photos: array()
      .of(
        object({
          path: string().defined(),
          smallPath: string().defined(),
          mediumPath: string().defined(),
          largePath: string().defined(),
          rawPath: string().defined(),
          description: string().ensure(),
          cloudinaryId: string().defined(),
        }),
      )
      .required()
      .min(1, t('externalListing.requiredPhotoMessage'))
      .max(10, t('externalListing.lessThan10PhotoMessage')),
    // listing tab
    size: number()
      .min(1, t('externalListing.sizeLabelError'))
      .nullable()
      .required()
      .label(t('externalListing.sizeLabel')),
    askingRent: number(),
    askingRentUnits: string().required(),
    condition: string().required().label(t('externalListing.condition')),
    leaseType: string().required().label(t('externalListing.leaseTypeLabel')),
    description: string().nullable(),
    availableImmediately: boolean().defined(),
    availabilityDate: string()
      .when(['availableImmediately'], {
        is: true,
        then: string().nullable().notRequired(),
        otherwise: string().nullable().required(),
      })
      .label(t('externalListing.available')),
    location: string().when(['longitude', 'latitude'], {
      is: (lng, lat) => !lng || !lat,
      then: string().required(t('externalListing.confirmMapError')),
      otherwise: string().notRequired().nullable(),
    }),
    submarket: string().notRequired().nullable(),
  });

export default function Form({
  tourbookId,
  children,
  onSubmit,
  isPromptEnabled,
  initialValues = {},
}: Props) {
  const { t } = useTranslation('tourbook');

  return (
    <SharedForm<FormValues>
      initialValues={{
        buildingName: null,
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        countryCode: 'US',
        longitude: null,
        latitude: null,
        photos: [],
        size: '',
        askingRent: undefined,
        askingRentUnits: 'Usd/sf/yr',
        condition: '',
        description: '',
        leaseType: '',
        availabilityDate: null,
        availableImmediately: false,
        submarket: '',
        ...initialValues,
      }}
      validationSchema={validationSchema(t)}
      id="createExternalTourbookListingForm"
      onSubmit={onSubmit}
    >
      <ImagesContextProvider>{children}</ImagesContextProvider>
      <FormCancellationPrompt enabled={isPromptEnabled} tourbookId={tourbookId} />
    </SharedForm>
  );
}
